import { useEffect, useState } from "react";
import { z } from "zod";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea
} from "@adora/shadcn-ui";
import { useToast } from "@adora/shared/hooks/use-toast";
import { useAdjustTokens } from "../../hooks/use-token-admin";

type WalletField = "set_monthly_allotment" | "set_rollover_cap";

interface AdjustWalletDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandId: number;
  field: WalletField;
  currentValue: number;
  /**
   * The current value of the *other* wallet field, used to enforce
   * rollover cap >= monthly allotment. When editing the rollover cap this is the
   * monthly allotment; when editing the monthly allotment this is the rollover cap.
   */
  counterpartValue: number;
}

const schema = z.object({
  newValue: z.number().int().min(0, "Must be 0 or greater"),
  reason: z.string().trim().min(1, "Reason is required").max(500)
});

const COPY: Record<WalletField, { title: string; label: string }> = {
  set_monthly_allotment: { title: "Adjust Monthly Allotment", label: "New monthly allotment" },
  set_rollover_cap: { title: "Adjust Rollover Cap", label: "New rollover cap" }
};

export default function AdjustWalletDialog({
  open,
  onOpenChange,
  brandId,
  field,
  currentValue,
  counterpartValue
}: AdjustWalletDialogProps) {
  const { openToast } = useToast();
  const { mutate, isPending } = useAdjustTokens(brandId);
  const [newValue, setNewValue] = useState("");
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Generated once per dialog-open and reused across retries of this submit so a
  // retried request is applied at most once server-side.
  const [idempotencyKey, setIdempotencyKey] = useState("");

  const copy = COPY[field];

  useEffect(() => {
    if (open) {
      setNewValue(String(currentValue));
      setReason("");
      setErrors({});
      setIdempotencyKey(crypto.randomUUID());
    }
  }, [open, currentValue]);

  const handleSubmit = () => {
    const parsed = schema.safeParse({ newValue: Number(newValue), reason });
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        fieldErrors[issue.path[0] as string] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    // Enforce rollover cap >= monthly allotment across the two fields.
    if (field === "set_rollover_cap" && parsed.data.newValue < counterpartValue) {
      setErrors({
        newValue: `Rollover cap must be at least the monthly allotment (${counterpartValue.toLocaleString()})`
      });
      return;
    }
    if (field === "set_monthly_allotment" && parsed.data.newValue > counterpartValue) {
      setErrors({
        newValue: `Monthly allotment can't exceed the rollover cap (${counterpartValue.toLocaleString()})`
      });
      return;
    }
    setErrors({});

    mutate(
      {
        brand_id: brandId,
        adjustment_type: field,
        amount: parsed.data.newValue,
        reason: parsed.data.reason,
        idempotency_key: idempotencyKey
      },
      {
        onSuccess: () => {
          openToast("Wallet updated");
          onOpenChange(false);
        },
        onError: (error: Error) => openToast(error.message || "Update failed")
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>Recorded in the audit ledger as an admin adjustment.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-value">{copy.label}</Label>
            <Input
              id="new-value"
              type="number"
              min={0}
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
            />
            {errors.newValue && <p className="text-sm text-destructive">{errors.newValue}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reason">Reason</Label>
            <Textarea
              id="reason"
              value={reason}
              placeholder="Recorded in the audit ledger"
              onChange={(e) => setReason(e.target.value)}
            />
            {errors.reason && <p className="text-sm text-destructive">{errors.reason}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
